import type { ParsedAngle } from "@/lib/tool-kit/geometry/angle-input";
import { piMultipleText } from "@/lib/tool-kit/geometry/angle-input";
import {
  type Exact,
  exactRational,
  exactSqrt,
  formatExact,
  scaleExact,
  toNumberExact,
} from "@/lib/tool-kit/geometry/exact";
import {
  type ExactTrig,
  exactTrigValues,
  formatTrigValue,
  normalizeDegrees,
  type Quadrant,
  quadrantOf,
  referenceAngle,
  TRIG_FUNCTIONS,
  type TrigFunction,
  trigDecimal,
} from "@/lib/tool-kit/geometry/trig-exact";
import {
  addR,
  divR,
  intR,
  negR,
  parseRational,
  type Rational,
  signR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/**
 * Trigonometric functions of an angle, and their inverses, with exact values wherever
 * the angle is a multiple of 15°.
 */

export interface TrigRow {
  fn: TrigFunction;
  /** Exact text, or null when only the decimal exists. */
  exact: string | null;
  /** Null where the function is undefined at this angle. */
  value: number | null;
}

export interface TrigForward {
  degrees: number;
  radians: number;
  /** Exact degrees when known. */
  exactDegrees: Rational | null;
  rows: TrigRow[];
  /** The angle brought into [0°, 360°). */
  coterminal: number;
  coterminalExact: Rational | null;
  /** On an axis (a multiple of 90°), an angle that belongs to no quadrant. */
  axis: boolean;
  quadrant: Quadrant;
  reference: number;
  referenceExact: Rational | null;
}

const NINETY = intR(90);
const TOLERANCE = 1e-9;

function floatNormalized(degrees: number): number {
  const turn = 360;
  const value = ((degrees % turn) + turn) % turn;
  return value === turn ? 0 : value;
}

function floatQuadrant(normalized: number): Quadrant {
  if (normalized <= 90) {
    return 1;
  }
  if (normalized <= 180) {
    return 2;
  }
  return normalized <= 270 ? 3 : 4;
}

function floatReference(normalized: number, quadrant: Quadrant): number {
  if (quadrant === 1) {
    return normalized;
  }
  if (quadrant === 2) {
    return 180 - normalized;
  }
  return quadrant === 3 ? normalized - 180 : 360 - normalized;
}

/** All six functions of an angle, exact when it is a multiple of 15°. */
export function solveForward(angle: ParsedAngle): TrigForward {
  const degrees = angle.degreesValue;
  const exactDegrees = angle.degrees;
  const exact: ExactTrig | null = exactDegrees
    ? exactTrigValues(exactDegrees)
    : null;
  const rows: TrigRow[] = TRIG_FUNCTIONS.map((fn) => {
    if (exact) {
      const value = exact[fn];
      return {
        fn,
        exact: formatTrigValue(value),
        value: value === "undefined" ? null : trigValueOf(value),
      };
    }
    return { fn, exact: null, value: trigDecimal(fn, degrees) };
  });
  const coterminalExact = exactDegrees ? normalizeDegrees(exactDegrees) : null;
  const coterminal = coterminalExact
    ? toNumberR(coterminalExact)
    : floatNormalized(degrees);
  const quadrant = coterminalExact
    ? quadrantOf(coterminalExact)
    : floatQuadrant(coterminal);
  const referenceExact = coterminalExact
    ? referenceAngle(coterminalExact)
    : null;
  const reference = referenceExact
    ? toNumberR(referenceExact)
    : floatReference(coterminal, quadrant);
  const axis = coterminalExact
    ? divR(coterminalExact, NINETY).den === BigInt(1)
    : false;
  return {
    degrees,
    radians: angle.radiansValue,
    exactDegrees,
    rows,
    coterminal,
    coterminalExact,
    axis,
    quadrant,
    reference,
    referenceExact,
  };
}

function trigValueOf(value: Exact): number {
  return toNumberExact(value);
}

/* ---------------------------------------------------------------- inverse */

export const INVERSE_FUNCTIONS = [
  "asin",
  "acos",
  "atan",
  "acsc",
  "asec",
  "acot",
] as const;
export type InverseFunction = (typeof INVERSE_FUNCTIONS)[number];

const FORWARD_OF: Record<InverseFunction, TrigFunction> = {
  asin: "sin",
  acos: "cos",
  atan: "tan",
  acsc: "csc",
  asec: "sec",
  acot: "cot",
};

/** Whole 15° steps in each inverse function's principal range. */
const STEP_RANGE: Record<
  InverseFunction,
  { from: number; to: number; skip: number[] }
> = {
  asin: { from: -6, to: 6, skip: [] },
  acos: { from: 0, to: 12, skip: [] },
  atan: { from: -5, to: 5, skip: [] },
  acsc: { from: -6, to: 6, skip: [0] },
  asec: { from: 0, to: 12, skip: [6] },
  acot: { from: 1, to: 11, skip: [] },
};

/** A number typed as a decimal, a fraction or a radical such as √3/2. */
export interface ExactInput {
  value: number;
  exact: Exact;
}

const RADICAL =
  /^([+\-−]?)\s*(\d*\.?\d*)\s*\*?\s*(?:√|sqrt)\s*\(?\s*(\d+)\s*\)?\s*(?:\/\s*(\d+\.?\d*))?$/i;

export function parseExactInput(text: string): ExactInput | null {
  const trimmed = text.trim();
  if (trimmed === "") {
    return null;
  }
  const match = RADICAL.exec(trimmed);
  if (match) {
    const negative = match[1] === "-" || match[1] === "−";
    const coefficient = match[2] === "" ? intR(1) : parseRational(match[2]);
    const radicand = parseRational(match[3]);
    const divisor = match[4] === undefined ? intR(1) : parseRational(match[4]);
    if (
      coefficient === null ||
      radicand === null ||
      divisor === null ||
      signR(divisor) === 0
    ) {
      return null;
    }
    const magnitude = divR(coefficient, divisor);
    const exact = scaleExact(
      exactSqrt(radicand),
      negative ? negR(magnitude) : magnitude
    );
    return { value: toNumberExact(exact), exact };
  }
  const rational = parseRational(trimmed);
  if (rational === null) {
    return null;
  }
  return { value: toNumberR(rational), exact: exactRational(rational) };
}

/** The principal value in degrees as a float. */
function principalDegrees(fn: InverseFunction, x: number): number {
  const deg = 180 / Math.PI;
  switch (fn) {
    case "asin":
      return Math.asin(x) * deg;
    case "acos":
      return Math.acos(x) * deg;
    case "atan":
      return Math.atan(x) * deg;
    case "acsc":
      return Math.asin(1 / x) * deg;
    case "asec":
      return Math.acos(1 / x) * deg;
    default: {
      if (x === 0) {
        return 90;
      }
      const base = Math.atan(1 / x) * deg;
      return x > 0 ? base : base + 180;
    }
  }
}

function inDomain(fn: InverseFunction, x: number): boolean {
  if (fn === "asin" || fn === "acos") {
    return Math.abs(x) <= 1;
  }
  if (fn === "acsc" || fn === "asec") {
    return Math.abs(x) >= 1;
  }
  return true;
}

/** The special angle (a multiple of 15°) whose function value is exactly `input`. */
function exactPrincipal(
  fn: InverseFunction,
  input: ExactInput
): Rational | null {
  const range = STEP_RANGE[fn];
  const forward = FORWARD_OF[fn];
  const target = formatExact(input.exact);
  for (let step = range.from; step <= range.to; step += 1) {
    if (range.skip.includes(step)) {
      continue;
    }
    const degrees = intR(step * 15);
    const value = exactTrigValues(degrees)?.[forward];
    if (
      value !== undefined &&
      value !== "undefined" &&
      formatExact(value) === target
    ) {
      return degrees;
    }
  }
  return null;
}

export interface InverseSolution {
  degrees: number;
  exact: Rational | null;
  /** "π/6" for exact angles; empty when only a decimal exists. */
  radians: string;
}

export interface InverseResult {
  ok: true;
  principal: InverseSolution;
  /** Every solution in [0°, 360°). */
  all: InverseSolution[];
}

export type InverseOutcome = InverseResult | { ok: false; error: "domain" };

function solution(exact: Rational | null, degrees: number): InverseSolution {
  return {
    degrees: exact ? toNumberR(exact) : degrees,
    exact,
    radians: exact ? piMultipleText(divR(exact, intR(180))) : "",
  };
}

function secondSolution(fn: InverseFunction, theta: number): number {
  if (fn === "asin" || fn === "acsc") {
    return 180 - theta;
  }
  if (fn === "acos" || fn === "asec") {
    return 360 - theta;
  }
  return theta + 180;
}

function secondExact(fn: InverseFunction, theta: Rational): Rational {
  if (fn === "asin" || fn === "acsc") {
    return addR(intR(180), negR(theta));
  }
  if (fn === "acos" || fn === "asec") {
    return addR(intR(360), negR(theta));
  }
  return addR(theta, intR(180));
}

/** The principal value of an inverse trig function, and every solution in one turn. */
export function solveInverse(
  fn: InverseFunction,
  input: ExactInput
): InverseOutcome {
  if (!inDomain(fn, input.value)) {
    return { ok: false, error: "domain" };
  }
  const exact = exactPrincipal(fn, input);
  const degrees = principalDegrees(fn, input.value);
  const principal = solution(exact, degrees);
  const first = exact ? normalizeDegrees(exact) : null;
  const firstValue = first ? toNumberR(first) : floatNormalized(degrees);
  const second = exact ? normalizeDegrees(secondExact(fn, exact)) : null;
  const secondValue = second
    ? toNumberR(second)
    : floatNormalized(secondSolution(fn, degrees));
  const candidates = [
    solution(first, firstValue),
    solution(second, secondValue),
  ];
  candidates.sort((a, b) => a.degrees - b.degrees);
  const all = candidates.filter(
    (item, index) =>
      index === 0 ||
      Math.abs(item.degrees - candidates[index - 1].degrees) > TOLERANCE
  );
  return { ok: true, principal, all };
}
